import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { toast } from 'sonner'

import { queryClient } from '@/App'
import { userAtom } from '@/store/store'
import { documentKeys } from '@/hooks/documents/documentsQueryKeys'
import { copyAllDocuments } from '@/api'

type CopyRequest = {
  sourceCourse: string
  targetCourse: string
  overwrite?: boolean
}

type CopyResponse = {
  sourceCourse: string
  targetCourse: string
  summary: {
    requested: number
    copied: number
    failed: number
  }
  results: Array<{ fileName: string; status: 'copied' | 'failed'; reason?: string }>
}

const useCopyCourseDocuments = (): UseMutationResult<CopyResponse, Error, CopyRequest, unknown> => {
  const { userId } = useAtomValue(userAtom)

  return useMutation({
    mutationFn: ({ sourceCourse, targetCourse, overwrite = false }) =>
      copyAllDocuments(userId, sourceCourse, targetCourse, overwrite),
    onSuccess: (data) => {
      const skipped = Math.max(data.summary.requested - data.summary.copied, 0)
      const skippedLabel = skipped > 0 ? `, ${skipped} skipped (already exists)` : ''

      toast.success('Copy complete', {
        description: `Copied ${data.summary.copied}/${data.summary.requested} documents to ${data.targetCourse}${skippedLabel}.`,
      })

      queryClient.invalidateQueries({ queryKey: documentKeys.byUser(userId) })
      queryClient.invalidateQueries({ queryKey: documentKeys.byCourse(userId, data.sourceCourse) })
      queryClient.invalidateQueries({ queryKey: documentKeys.byCourse(userId, data.targetCourse) })
    },
    onError: (error) => {
      toast.error('Copy failed', {
        description: error instanceof Error ? error.message : 'Please try again',
      })
    },
  })
}

export default useCopyCourseDocuments
